import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { PlatformMessageAttachment } from '@liks/backend-svc-api/esm/models/index';
import { useI18n } from '../../../i18n/useI18n';
import { resolveApiErrorMessage } from '../../../query/apiErrorMessage';
import { platformChatService } from '../../../services/PlatformChatService';
import { toast } from '../../../toast/toast';
import { chatQueryKeys } from '../chatQueryKeys';
import { resolveUploadAttachmentType } from '../platformChatAttachmentUtils';
import { usePlatformChatSessionDraftAttachmentsQuery } from '../queries/usePlatformChatSessionDraftAttachmentsQuery';

export type PlatformChatStagedAttachmentStatus =
  'uploading' | 'ready' | 'error';

export type PlatformChatStagedAttachment = {
  id: string;
  fileName: string;
  attachmentId?: string;
  status: PlatformChatStagedAttachmentStatus;
};

function createPendingAttachmentId(file: File): string {
  return `pending-${file.name}-${file.size}-${file.lastModified}-${crypto.randomUUID()}`;
}

function resolveDraftAttachmentName(
  attachment: PlatformMessageAttachment & { id: string },
  fallbackLabel: string,
): string {
  const documentName = attachment.documentName?.trim();
  if (documentName) {
    return documentName;
  }

  const fileName = attachment.fileName?.trim();
  if (fileName) {
    return fileName;
  }

  return fallbackLabel;
}

function mapDraftAttachmentToStaged(
  attachment: PlatformMessageAttachment & { id: string },
  fallbackLabel: string,
): PlatformChatStagedAttachment {
  return {
    id: `draft-${attachment.id}`,
    fileName: resolveDraftAttachmentName(attachment, fallbackLabel),
    attachmentId: attachment.id,
    status: 'ready',
  };
}

export function usePlatformChatStagedAttachments(sessionId: string) {
  const queryClient = useQueryClient();
  const { translate } = useI18n();
  const draftAttachmentsQuery =
    usePlatformChatSessionDraftAttachmentsQuery(sessionId);
  const [pendingAttachments, setPendingAttachments] = useState<
    PlatformChatStagedAttachment[]
  >([]);
  const cancelledUploadIdsRef = useRef(new Set<string>());
  const unknownAttachmentLabel = translate('platformChat.unknownAttachment');

  const serverAttachments = useMemo(
    () =>
      (draftAttachmentsQuery.data ?? []).map((attachment) =>
        mapDraftAttachmentToStaged(attachment, unknownAttachmentLabel),
      ),
    [draftAttachmentsQuery.data, unknownAttachmentLabel],
  );

  const stagedAttachments = useMemo(() => {
    const serverAttachmentIds = new Set(
      serverAttachments.flatMap((attachment) =>
        attachment.attachmentId ? [attachment.attachmentId] : [],
      ),
    );

    const unresolvedPendingAttachments = pendingAttachments.filter(
      (attachment) =>
        attachment.status === 'uploading' ||
        attachment.status === 'error' ||
        (attachment.attachmentId
          ? !serverAttachmentIds.has(attachment.attachmentId)
          : true),
    );

    return [...serverAttachments, ...unresolvedPendingAttachments];
  }, [pendingAttachments, serverAttachments]);

  const hasUploadingAttachments = useMemo(
    () =>
      stagedAttachments.some((attachment) => attachment.status === 'uploading'),
    [stagedAttachments],
  );

  const attachmentIds = useMemo(
    () =>
      stagedAttachments.flatMap((attachment) =>
        attachment.status === 'ready' && attachment.attachmentId
          ? [attachment.attachmentId]
          : [],
      ),
    [stagedAttachments],
  );

  const invalidateDraftAttachments = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: chatQueryKeys.sessionDraftAttachments(sessionId),
    });
  }, [queryClient, sessionId]);

  const addFiles = useCallback(
    (files: File[]) => {
      if (sessionId.length === 0 || files.length === 0) {
        return;
      }

      for (const file of files) {
        const localId = createPendingAttachmentId(file);
        setPendingAttachments((current) => [
          ...current,
          {
            id: localId,
            fileName: file.name,
            status: 'uploading',
          },
        ]);

        void (async () => {
          try {
            const uploaded =
              await platformChatService.uploadSessionDraftAttachment(
                sessionId,
                file,
                resolveUploadAttachmentType(file),
              );

            if (cancelledUploadIdsRef.current.has(localId)) {
              cancelledUploadIdsRef.current.delete(localId);
              try {
                await platformChatService.deleteSessionDraftAttachment(
                  sessionId,
                  uploaded.id,
                );
              } catch {
                // Best-effort cleanup for uploads removed while in flight.
              }
              invalidateDraftAttachments();
              return;
            }

            setPendingAttachments((current) =>
              current.filter((attachment) => attachment.id !== localId),
            );
            invalidateDraftAttachments();
          } catch (error) {
            const apiMessage = await resolveApiErrorMessage(error);
            toast.error({
              title: translate('platformChat.uploadAttachmentErrorTitle'),
              description:
                apiMessage !== ''
                  ? apiMessage
                  : translate('platformChat.uploadAttachmentErrorDescription', {
                      fileName: file.name,
                    }),
            });

            if (cancelledUploadIdsRef.current.has(localId)) {
              cancelledUploadIdsRef.current.delete(localId);
              return;
            }

            setPendingAttachments((current) =>
              current.map((attachment) =>
                attachment.id === localId
                  ? { ...attachment, status: 'error' }
                  : attachment,
              ),
            );
          }
        })();
      }
    },
    [invalidateDraftAttachments, sessionId, translate],
  );

  const removeAttachment = useCallback(
    (localId: string) => {
      cancelledUploadIdsRef.current.add(localId);

      const stagedAttachment = stagedAttachments.find(
        (attachment) => attachment.id === localId,
      );

      setPendingAttachments((current) =>
        current.filter((attachment) => attachment.id !== localId),
      );

      if (stagedAttachment?.attachmentId) {
        void platformChatService
          .deleteSessionDraftAttachment(
            sessionId,
            stagedAttachment.attachmentId,
          )
          .then(() => {
            invalidateDraftAttachments();
          })
          .catch(async (error) => {
            const apiMessage = await resolveApiErrorMessage(error);
            toast.error({
              title: translate('platformChat.removeAttachmentErrorTitle'),
              description:
                apiMessage !== ''
                  ? apiMessage
                  : translate('platformChat.removeAttachmentErrorDescription', {
                      fileName: stagedAttachment.fileName,
                    }),
            });
            invalidateDraftAttachments();
          });
      }
    },
    [invalidateDraftAttachments, sessionId, stagedAttachments, translate],
  );

  const clearAttachments = useCallback(() => {
    cancelledUploadIdsRef.current.clear();
    setPendingAttachments([]);
    queryClient.setQueryData(
      chatQueryKeys.sessionDraftAttachments(sessionId),
      [],
    );
  }, [queryClient, sessionId]);

  return {
    stagedAttachments,
    attachmentIds,
    hasUploadingAttachments,
    isDraftAttachmentsLoading: draftAttachmentsQuery.isLoading,
    addFiles,
    removeAttachment,
    clearAttachments,
  };
}
